import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip, Select, message } from "antd";
import { Helmet } from "react-helmet";
import { paginate } from "../../../utlits/Paginate.js";
import Pagination from "../../Reuseable/Pagination";
import MachineIntegrationService from "../../../Services/MachineIntegrationService";
import DataParser from "../../../utlits/Dataparser";
import StatusBadge from "../../Reuseable/StatusBadge";
import SocketService from "../../../Services/SocketService";
import TableSkeleton from "../../Reuseable/TableSkeleton";

const { Option } = Select;
message.config({ zIndex: 1000000001 });

const ConnectionBadge = ({ machineId }) => {
    const [status, setStatus] = useState("checking");

    const check = async () => {
        try {
            setStatus("checking");
            const res = await MachineIntegrationService.testConnection(machineId);
            setStatus(res.Good && res.data.connected ? "connected" : "disconnected");
        } catch {
            setStatus("disconnected");
        }
    };

    useEffect(() => { check(); }, [machineId]);

    if (status === "checking") return <span className="badge bg-warning text-dark"><i className="bx bx-loader bx-spin me-1" />Checking...</span>;
    if (status === "connected") return <span className="badge bg-success" onClick={check} style={{ cursor: "pointer" }}><i className="bx bx-check-circle me-1" />Connected</span>;
    return <span className="badge bg-danger" onClick={check} style={{ cursor: "pointer" }}><i className="bx bx-x-circle me-1" />Disconnected</span>;
};

const MachineIntegration = () => {
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [userPermissions, setuserPermissions] = useState([]);
    const [itemToDelete, setItemToDelete] = useState(null);

    const [name, setName] = useState("");
    const [ip, setIp] = useState("");
    const [port, setPort] = useState(4370);
    const [commKey, setCommKey] = useState("0");
    const [error, setError] = useState("");

    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editIp, setEditIp] = useState("");
    const [editPort, setEditPort] = useState(4370);
    const [editCommKey, setEditCommKey] = useState("0");

    const compid = DataParser.CompanyId();

    const fetchMachines = async () => {
        setLoading(true);
        try {
            const res = await MachineIntegrationService.getMachines(compid);
            if (res.Good) setMachines(res.data.machines || []);
        } catch (err) {
            console.error("Error fetching machines:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMachines();
        setuserPermissions(DataParser.CompanyUserPermissions() || []);
    }, []);

    useEffect(() => {
        SocketService.connect();
        const handleMachineUpdate = (data) => {
            if (data.company_id === compid) fetchMachines();
        };
        SocketService.on("machine_update", handleMachineUpdate);
        return () => SocketService.off("machine_update", handleMachineUpdate);
    }, [compid]);

    const handelPageChange = (e, page) => {
        e.preventDefault();
        setCurrentPage(page);
    };

    const clearCreateState = () => { setName(""); setIp(""); setPort(4370); setCommKey("0"); setError(""); };
    const clearEditState = () => { setEditName(""); setEditIp(""); setEditPort(4370); setEditCommKey("0"); setEditId(null); setError(""); };

    const createMachine = (e) => {
        e.preventDefault();
        if (!name || !ip || !port) { setError("Please fill in all required fields."); return; }
        MachineIntegrationService.createMachine({ company_id: compid, machine_name: name, device_ip: ip, device_port: port, communication_key: commKey, status: 1 })
            .then((res) => {
                if (res?.Good === true) {
                    message.success("Machine added successfully!");
                    document.getElementById("createMachineModalClose").click();
                    clearCreateState();
                    fetchMachines();
                } else {
                    message.error(res?.data || "Failed to add machine!");
                }
            })
            .catch(() => message.error("Something went wrong!"));
    };

    const handelEdit = (e, item) => {
        e.preventDefault();
        setEditName(item.machine_name);
        setEditIp(item.device_ip);
        setEditPort(item.device_port);
        setEditCommKey(item.communication_key);
        setEditId(item.id);
        setError("");
    };

    const handelUpdate = (e) => {
        e.preventDefault();
        if (!editName || !editIp || !editPort) { setError("Please fill in all required fields."); return; }
        MachineIntegrationService.updateMachine(editId, { machine_name: editName, device_ip: editIp, device_port: editPort, communication_key: editCommKey })
            .then((res) => {
                if (res?.Good === true) {
                    message.success("Machine updated successfully!");
                    document.getElementById("editMachineModalClose").click();
                    clearEditState();
                    fetchMachines();
                } else {
                    message.error("Failed to update machine!");
                }
            })
            .catch(() => message.error("Something went wrong!"));
    };

    const handelDelete = (e, item) => {
        e.preventDefault();
        setItemToDelete(item);
        document.getElementById("openDeleteMachineModal").click();
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        document.getElementById("closeMachineDeleteModal").click();
        try {
            const res = await MachineIntegrationService.deleteMachine(itemToDelete.id, compid);
            if (res?.Good === true) {
                message.success("Machine deleted successfully!");
                fetchMachines();
            } else {
                message.error("Failed to delete machine!");
            }
        } catch {
            message.error("Something went wrong!");
        }
        setItemToDelete(null);
    };

    const PaginatedData = paginate(machines, currentPage, pageSize);

    return (
        <>
            <Helmet>
                <title>Machine Integration - Planit</title>
            </Helmet>
            <div className="main-content">
                <div className="page-content Pagebody" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12 px-0">
                                <div className="d-flex align-items-end justify-content-end" style={{ padding: "0 0 12px 0" }}>
                                    <div className="d-flex flex-column align-items-end" style={{ gap: "6px" }}>
                                        <ol className="breadcrumb mb-0">
                                            <li className="breadcrumb-item">
                                                <Link to={`/${DataParser.CompanyIdentifierRole()}/employee-attendance`}>Home</Link>
                                            </li>
                                            <li className="breadcrumb-item active">
                                                <Link to={`/${DataParser.CompanyIdentifierRole()}/settings`}>Settings</Link>
                                            </li>
                                            <li className="breadcrumb-item active">Machine Integration</li>
                                        </ol>
                                        {userPermissions.indexOf("machine-integration-create") !== -1 ? (
                                            <button
                                                type="button"
                                                className="btn btn-primary waves-effect waves-light"
                                                style={{ borderRadius: "5px" }}
                                                data-bs-toggle="modal"
                                                data-bs-target="#createMachineModal"
                                            >
                                                Add Machine
                                            </button>
                                        ) : (
                                            <button type="button" className="btn btn-primary waves-effect waves-light" disabled style={{ opacity: 0.6, cursor: "not-allowed", borderRadius: "5px" }}>
                                                Add Machine
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-12 px-0">
                                <div className="card" style={{ backgroundColor: "#fff", boxShadow: "none", borderRadius: 0, border: "none" }}>
                                    <div className="card-body p-0">
                                        <div className="table-responsive listing-table-wrapper">
                                            <table className="table mb-0 custom-table listing-table">
                                                <thead>
                                                    <tr className="tableHeaderbg">
                                                        <th>Sr#</th>
                                                        <th>Machine Name</th>
                                                        <th>Device IP</th>
                                                        <th>Device Port</th>
                                                        <th>Comm. Key</th>
                                                        <th>Connection</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {loading ? (
                                                        <TableSkeleton cols={8} rows={6} />
                                                    ) : PaginatedData.length > 0 ? (
                                                        PaginatedData.map((item, index) => (
                                                            <tr key={item.id}>
                                                                <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                                                <td>{item.machine_name}</td>
                                                                <td>{item.device_ip}</td>
                                                                <td>{item.device_port}</td>
                                                                <td>{item.communication_key}</td>
                                                                <td><ConnectionBadge machineId={item.id} /></td>
                                                                <td>
                                                                    <StatusBadge item={item} api={MachineIntegrationService.statusUpdate} />
                                                                </td>
                                                                <td>
                                                                    {userPermissions.indexOf("machine-integration-delete") !== -1 ? (
                                                                        <Link to="#" className="cursor-pointer iconscolor" onClick={(e) => handelDelete(e, item)}>
                                                                            <Tooltip title="Delete Machine">
                                                                                <i className="uil-trash iconscolor" />
                                                                            </Tooltip>
                                                                        </Link>
                                                                    ) : (
                                                                        <span className="uil-trash iconscolor text-muted" style={{ cursor: "not-allowed", opacity: 0.5 }} />
                                                                    )}
                                                                    {userPermissions.indexOf("machine-integration-edit") !== -1 ? (
                                                                        <Link
                                                                            to="#"
                                                                            className="cursor-pointer iconscolor"
                                                                            onClick={(e) => handelEdit(e, item)}
                                                                            data-bs-toggle="modal"
                                                                            data-bs-target="#editMachineModal"
                                                                        >
                                                                            <Tooltip title="Edit Machine">
                                                                                <i className="uil-pen iconscolor" />
                                                                            </Tooltip>
                                                                        </Link>
                                                                    ) : (
                                                                        <span className="uil-pen iconscolor text-muted" style={{ cursor: "not-allowed", opacity: 0.5 }} />
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="8" className="text-center">
                                                                No machines found. Click 'Add Machine' to add one.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>

                                            {machines.length > pageSize && (
                                                <div className="d-flex justify-content-center mt-4">
                                                    <Pagination
                                                        itemCount={machines.length}
                                                        pageSize={pageSize}
                                                        currentPage={currentPage}
                                                        onPageChange={handelPageChange}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Hidden trigger for delete modal */}
                <div id="openDeleteMachineModal" data-bs-toggle="modal" data-bs-target="#deleteMachineModal" style={{ display: "none" }} />

                {/* Delete Confirmation Modal */}
                <div id="deleteMachineModal" className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true" data-bs-keyboard="false">
                    <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
                        <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
                            <div className="modal-header border-0 pb-1">
                                <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Delete Machine</h5>
                                <button type="button" className="btn-close" id="closeMachineDeleteModal" data-bs-dismiss="modal" aria-label="Close" onClick={() => setItemToDelete(null)} />
                            </div>
                            <div className="modal-body pt-2 pb-2">
                                <p style={{ fontSize: "14px", color: "#1D1D1D", marginBottom: "6px" }}>Are you sure you want to delete this machine?</p>
                                <p style={{ fontSize: "13px", color: "#ff4d4f", marginBottom: 0 }}>
                                    <i className="uil uil-exclamation-triangle me-1" />
                                    This action cannot be undone.
                                </p>
                            </div>
                            <div className="modal-footer border-0 pt-1">
                                <button type="button" className="btn btn-danger waves-effect waves-light" onClick={confirmDelete} style={{ borderRadius: "5px", fontSize: "13px" }}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Create Modal */}
                <div id="createMachineModal" className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true" data-bs-keyboard="false">
                    <form onSubmit={createMachine}>
                        <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
                            <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
                                <div className="modal-header border-0 pb-1">
                                    <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Add New Machine</h5>
                                    <button type="button" className="btn-close" id="createMachineModalClose" data-bs-dismiss="modal" aria-label="Close" onClick={clearCreateState} />
                                </div>
                                <div className="modal-body pt-2 pb-2">
                                    {error && <small className="text-danger d-block mb-2">{error}</small>}
                                    <div className="mb-3">
                                        <div className={`float-select-wrap ${name ? "has-value" : ""}`}>
                                            <label>Machine Name*</label>
                                            <Select placeholder="" value={name || undefined} onChange={(v) => { setName(v); setError(""); }}>
                                                <Option value="ZKTeco">ZKTeco</Option>
                                                <Option value="Dahua">Dahua</Option>
                                                <Option value="Hikvision">Hikvision</Option>
                                                <Option value="Other">Other</Option>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="float-input-wrap">
                                            <input type="text" placeholder=" " value={ip} onChange={(e) => { setIp(e.target.value); setError(""); }} required />
                                            <label>Device IP*</label>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="float-input-wrap">
                                            <input type="number" placeholder=" " value={port} onChange={(e) => { setPort(e.target.value); setError(""); }} required />
                                            <label>Device Port*</label>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="float-input-wrap">
                                            <input type="text" placeholder=" " value={commKey} onChange={(e) => setCommKey(e.target.value)} />
                                            <label>Communication Key</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 pt-1">
                                    <button type="submit" className="btn btn-primary waves-effect waves-light" style={{ borderRadius: "5px", fontSize: "13px" }}>
                                        + Add Machine
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Edit Modal */}
                <div id="editMachineModal" className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true" data-bs-keyboard="false">
                    <form onSubmit={handelUpdate}>
                        <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
                            <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
                                <div className="modal-header border-0 pb-1">
                                    <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Edit Machine</h5>
                                    <button type="button" className="btn-close" id="editMachineModalClose" data-bs-dismiss="modal" aria-label="Close" onClick={clearEditState} />
                                </div>
                                <div className="modal-body pt-2 pb-2">
                                    {error && <small className="text-danger d-block mb-2">{error}</small>}
                                    <div className="mb-3">
                                        <div className={`float-select-wrap ${editName ? "has-value" : ""}`}>
                                            <label>Machine Name*</label>
                                            <Select placeholder="" value={editName || undefined} onChange={(v) => { setEditName(v); setError(""); }}>
                                                <Option value="ZKTeco">ZKTeco</Option>
                                                <Option value="Dahua">Dahua</Option>
                                                <Option value="Hikvision">Hikvision</Option>
                                                <Option value="Other">Other</Option>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="float-input-wrap">
                                            <input type="text" placeholder=" " value={editIp} onChange={(e) => { setEditIp(e.target.value); setError(""); }} required />
                                            <label>Device IP*</label>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="float-input-wrap">
                                            <input type="number" placeholder=" " value={editPort} onChange={(e) => { setEditPort(e.target.value); setError(""); }} required />
                                            <label>Device Port*</label>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="float-input-wrap">
                                            <input type="text" placeholder=" " value={editCommKey} onChange={(e) => setEditCommKey(e.target.value)} />
                                            <label>Communication Key</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 pt-1">
                                    <button type="submit" className="btn btn-primary waves-effect waves-light" style={{ borderRadius: "5px", fontSize: "13px" }}>
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default MachineIntegration;
